import PropTypes from 'prop-types';
import styled from 'styled-components';

import { config } from '_config';
import { theme } from '_constants/theme';

import * as Layout from '_components/atomic/layouts';
import JoinBanner from '../JoinBanner';

export const benefitsOfFormingAnLlcForSmallBusinessesContents = [
  'What is an LLC',
  'Key Benefits of Forming an LLC',
  'How to form an LLC',
];

const StyledContainer = styled.div`
  position: relative;
`;

const StyledBlock = styled.div`
  position: relative;
  z-index: 1;

  ol,
  ul {
    padding-top: ${theme.spaces.regular}px;

    li {
      margin-bottom: ${theme.spaces.regular}px;

      &::marker {
        font-weight: 700;
      }
    }
  }
`;

const StyledBackgroundImageContainer = styled.div`
  position: absolute;
  top: 40%;
  width: 100%;

  img {
    width: 100%;
  }
`;

const StyledImageContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${theme.spaces.large4X}px;
`;

const BenefitsOfFormingAnLlcForSmallBusinessesArticle = ({ idPrefix }) => {
  return (
    <StyledContainer>
      <Layout.Card.Text>
        Starting a small business involves making numerous important decisions,
        one of which is choosing the right business structure. One popular
        option for many entrepreneurs is forming a Limited Liability Company
        (LLC). This article explores the key benefits of forming an LLC for
        small businesses, helping you understand why it might be the best choice
        for your venture.
      </Layout.Card.Text>
      <StyledBlock id={`${idPrefix}-${0}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          What is an LLC?
        </Layout.Card.Text>
        <Layout.Card.Text>
          A Limited Liability Company (LLC) is a business structure that
          combines the limited liability protection of a corporation with the
          flexibility and tax advantages of a partnership or sole
          proprietorship. It is a popular choice among small business owners due
          to its simplicity and benefits.
        </Layout.Card.Text>
        <StyledImageContainer>
          <picture>
            <source
              srcSet={`${config.basePath}/images/blog-what-is-llc.svg`}
              type="image/svg+xml"
            />
            <img
              src={`${config.basePath}/images/blog-what-is-llc.svg`}
              alt="Background Logo"
            />
          </picture>
        </StyledImageContainer>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${1}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Key Benefits of Forming an LLC
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest centered noBottomSpace>
          1. Limited Liability Protection
        </Layout.Card.Text>
        <Layout.Card.Text>
          One of the most significant advantages of forming an LLC is the
          limited liability protection it provides. As an LLC owner, your
          personal assets are generally protected from business debts and
          lawsuits. This means that creditors can only pursue the assets of the
          business, not your personal property.
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest centered noBottomSpace>
          2. Pass-Through Taxation
        </Layout.Card.Text>
        <Layout.Card.Text>
          LLCs benefit from pass-through taxation, meaning the business itself
          is not taxed on its profits. Instead, profits and losses are passed
          through to the owners, who report them on their personal tax returns.
          This avoids the double taxation faced by corporations, where profits
          are taxed at both the corporate and individual levels.
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest centered noBottomSpace>
          3. Flexibility in Management
        </Layout.Card.Text>
        <Layout.Card.Text>
          LLCs offer flexibility in management and operations. They can be
          managed by the owners (members) or by appointed managers. This allows
          small business owners to structure their management in a way that best
          suits their needs and preferences.
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest centered noBottomSpace>
          4. Simple Formation and Compliance
        </Layout.Card.Text>
        <Layout.Card.Text>
          Forming an LLC is generally simpler and less costly than forming a
          corporation. The paperwork and regulatory requirements are often
          minimal, making it easier for small business owners to comply with
          state laws and regulations.
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest centered noBottomSpace>
          5. Enhanced Credibility
        </Layout.Card.Text>
        <Layout.Card.Text>
          Operating as an LLC can enhance your business&apos;s credibility with
          customers, suppliers, and potential investors. It shows that you are
          serious about your business and have taken steps to protect your
          personal assets and meet legal requirements.
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest centered noBottomSpace>
          6. Flexible Profit Distribution
        </Layout.Card.Text>
        <Layout.Card.Text>
          Unlike corporations, which must distribute profits based on the number
          of shares held by each shareholder, LLCs have flexibility in how they
          distribute profits among members. This allows for customized
          profit-sharing arrangements that can reflect each member&apos;s
          contribution to the business.
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest centered noBottomSpace>
          7. Perpetual Existence
        </Layout.Card.Text>
        <Layout.Card.Text>
          An LLC has a perpetual existence, meaning it continues to exist even
          if the ownership changes. This can be beneficial for long-term
          business planning and stability, as the business does not dissolve
          when an owner leaves or passes away.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${2}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          How to Form an LLC
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest centered noBottomSpace>
          1. Choose a Business Name
        </Layout.Card.Text>
        <Layout.Card.Text>
          Select a unique name for your LLC that complies with your state&apos;s
          naming requirements. Ensure the name is distinguishable from other
          registered businesses in your state.
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest centered noBottomSpace>
          2. File Articles of Organization
        </Layout.Card.Text>
        <Layout.Card.Text>
          Submit the Articles of Organization to your state&apos;s Secretary of
          State office. This document includes basic information about your LLC,
          such as its name, address, and members.
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest centered noBottomSpace>
          3. Create an Operating Agreement
        </Layout.Card.Text>
        <Layout.Card.Text>
          Although not required in all states, an Operating Agreement is a
          crucial document that outlines the ownership and management structure
          of your LLC. It helps prevent disputes and ensures smooth operations.
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest centered noBottomSpace>
          4. Obtain Necessary Licenses and Permits
        </Layout.Card.Text>
        <Layout.Card.Text>
          Depending on your business type and location, you may need to obtain
          various licenses and permits to operate legally. Check with your local
          and state authorities to determine the requirements.
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest centered noBottomSpace>
          5. Register for Taxes
        </Layout.Card.Text>
        <Layout.Card.Text>
          Obtain an Employer Identification Number (EIN) from the IRS and
          register for any state and local taxes your business may be subject
          to. This includes sales tax, employment tax, and any industry-specific
          taxes.
        </Layout.Card.Text>
      </StyledBlock>
      <Layout.Card.Text>
        Forming an LLC offers numerous benefits for small business owners,
        including limited liability protection, pass-through taxation,
        management flexibility, and enhanced credibility. By understanding these
        advantages and following the necessary steps to form an LLC, you can set
        your business up for success and protect your personal assets. By
        choosing to form an LLC, small business owners can enjoy a range of
        benefits that support growth and stability. Whether you&apos;re just
        starting out or looking to restructure your existing business, an LLC
        might be the ideal solution for your needs.
      </Layout.Card.Text>
      <JoinBanner />
      <StyledBackgroundImageContainer>
        <picture>
          <source
            srcSet={`${config.basePath}/images/blog-arrow-logo2.svg`}
            type="image/svg+xml"
          />
          <img
            src={`${config.basePath}/images/blog-arrow-logo2.svg`}
            alt="Background Logo"
          />
        </picture>
      </StyledBackgroundImageContainer>
    </StyledContainer>
  );
};

BenefitsOfFormingAnLlcForSmallBusinessesArticle.propTypes = {
  idPrefix: PropTypes.string,
};

export default BenefitsOfFormingAnLlcForSmallBusinessesArticle;
